import { createInertiaApp, router } from '@inertiajs/react'
import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { reactErrorHandler, scheduleSentry } from './sentry'
import { csrfToken, syncCsrfToken } from '../lib/csrf'
import { reportAdConversions } from '../lib/adConversions'

const preloadRecoveryKey = 'tinker:preload-recovery'
const homeReloadScrollKey = 'tinker:home-reload-scroll'

const reactErrorHandlers = {
  onUncaughtError: reactErrorHandler,
  onCaughtError: reactErrorHandler,
  onRecoverableError: reactErrorHandler,
}

const preloadRecoveryAttempted = () => {
  try {
    return window.sessionStorage.getItem(preloadRecoveryKey) === '1'
  } catch {
    // Storage is denied in some embedded/private browsing contexts. Treat recovery as already
    // attempted: reloading without a durable sentinel could strand the page in a reload loop.
    return true
  }
}

const markPreloadRecoveryAttempted = () => {
  try {
    window.sessionStorage.setItem(preloadRecoveryKey, '1')
    return true
  } catch {
    return false
  }
}

const clearPreloadRecoveryAttempt = () => {
  try {
    window.sessionStorage.removeItem(preloadRecoveryKey)
  } catch {
    // The application is still usable when the browser denies optional recovery storage.
  }
}

const recoverFromStaleBundle = () => {
  if (preloadRecoveryAttempted()) return false
  if (!markPreloadRecoveryAttempted()) return false

  window.location.reload()
  return true
}

if (typeof document !== 'undefined') {
  scheduleSentry()

  const saveHomeReloadScroll = () => {
    try {
      if (window.location.pathname === '/' && window.scrollY > 0) {
        window.sessionStorage.setItem(homeReloadScrollKey, JSON.stringify({
          url: window.location.pathname + window.location.search,
          top: window.scrollY,
          left: window.scrollX,
          savedAt: Date.now(),
        }))
      } else {
        window.sessionStorage.removeItem(homeReloadScrollKey)
      }
    } catch {
      // Scroll restoration still falls back to Inertia's history state when storage is denied.
    }
  }
  let homeScrollSaveTimer = null
  window.addEventListener('scroll', () => {
    window.clearTimeout(homeScrollSaveTimer)
    homeScrollSaveTimer = window.setTimeout(saveHomeReloadScroll, 100)
  }, { passive: true })
  window.addEventListener('pagehide', saveHomeReloadScroll)

  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault()
    recoverFromStaleBundle()
  })

  // A chunk that fails to load fires vite:preloadError, but a page added after this bundle was
  // built is missing from the resolver's glob entirely — Inertia rejects with "Page not found"
  // and the visit dies. Same cause, same cure: reload once onto the current bundle.
  window.addEventListener('unhandledrejection', (event) => {
    if (!/^Page not found: /.test(event.reason?.message || '')) return
    if (recoverFromStaleBundle()) event.preventDefault()
  })

  router.on('success', (event) => {
    syncCsrfToken(event.detail.page.props.csrf_token)
    reportAdConversions(event.detail.page.props.ad_conversions)
  })
}

createInertiaApp({
  pages: "../pages",

  setup: ({ el, App, props }) => {
    clearPreloadRecoveryAttempt()
    syncCsrfToken(props.initialPage.props.csrf_token)
    reportAdConversions(props.initialPage.props.ad_conversions)

    const app = <StrictMode><App {...props} /></StrictMode>

    if (!el) return app

    if (el.hasAttribute("data-server-rendered")) {
      return hydrateRoot(el, app, reactErrorHandlers)
    }

    return createRoot(el, reactErrorHandlers).render(app)
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: (_href, options) => {
      const token = csrfToken()
      return {
        queryStringArrayFormat: "brackets",
        headers: {
          ...(options.headers || {}),
          ...(token ? { "X-CSRF-Token": token } : {}),
        },
      }
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_javascript_tag "inertia.jsx" %> to the Inertia-specific layout instead.',
    )
  }
})
